import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { InputModuleService } from '../../backend/inputModule';
import { AxiosError } from 'axios';

interface HolidayListState {
    holidayListDetails: {
        status: string,
        data: any,
        error: string | null
    },
    deleteHolidayDetails: {
        status: string,
        data: any,
        error: string | null
    },
    addHolidayDetails: {
        status: string,
        data: any,
        error: string | null
    },
    uploadHolidayDetails: {
        status: string,
        data: any,
        error: string | null
    },
    editHolidayDetails: {
        status: string,
        data: any,
        error: string | null
    },
    bulkDeleteHolidaysDetails: {
        status: string,
        data: any,
        error: string | null
    }
}

const initialState: HolidayListState = { 
    holidayListDetails: {
        status: 'idle',
        data: {
          items: [],
          meta: {}
        },
        error: null
    },
    deleteHolidayDetails: {
        status: 'idle',
        data: '',
        error: null
    },
    addHolidayDetails: {
        status: 'idle',
        data: '',
        error: null
    },
    uploadHolidayDetails: {
        status: 'idle',
        data: '',
        error: null
    },
    editHolidayDetails: {
        status: 'idle',
        data: '',
        error: null
    },
    bulkDeleteHolidaysDetails: {
        status: 'idle',
        data: '',
        error: null
    }
} as HolidayListState

export const getHolidaysList = createAsyncThunk('holidayList/getHolidaysList', async (data: any) => {
    const inputModuleService = new InputModuleService();
    return await inputModuleService.fetchHolidaysList(data);
})

export const deleteHoliday = createAsyncThunk('holidayList/deleteHoliday', async (id: any) => {
    const inputModuleService = new InputModuleService();
    return await inputModuleService.deleteHoliday(id);
})

export const addHoliday = createAsyncThunk('holidayList/addHoliday', async (data: any, { rejectWithValue }) => {
    try {
        const inputModuleService = new InputModuleService();
        return await inputModuleService.addHoliday(data);
    } catch (error: any) {
        const errorMessages = error && error.response && error.response.data && error.response.data.error_messages;
        if (errorMessages && errorMessages.length > 0) {
            return rejectWithValue(errorMessages.join(", "));
        }
        return rejectWithValue(null);
    }
})

export const uploadHoliday = createAsyncThunk('holidayList/uploadHoliday', async (data: any, { rejectWithValue }) => {
    try {
        const inputModuleService = new InputModuleService();
        return await inputModuleService.uploadHoliday(data);
    } catch (error: any) {
        if (error.response && error.response.data instanceof Blob) {
            try {
                const text = await error.response.data.text();
                const json = JSON.parse(text);
                return rejectWithValue(json.message);
            } catch {
                return rejectWithValue(null);
            }
        }
        return rejectWithValue(null);
    }
})

export const editHoliday = createAsyncThunk('holidayList/editHoliday', async (data: any, { rejectWithValue }) => {
    try {
        const inputModuleService = new InputModuleService();
        return await inputModuleService.editHoliday(data);
    } catch (error: any) {
        const errorMessages = error && error.response && error.response.data && error.response.data.error_messages;
        if (errorMessages && errorMessages.length > 0) {
            return rejectWithValue(errorMessages.join(", "));
        }
        return rejectWithValue(null);
    }
})

export const bulkDeleteHolidays = createAsyncThunk('holidayList/bulkDeleteHolidays', async (id: any) => {
    const inputModuleService = new InputModuleService();
    return await inputModuleService.bulkDeleteHolidays(id);
})

export const holidayListSlice = createSlice({
    name: 'holidayList',
    initialState,
    reducers: {
        resetGetHolidayDetailsStatus: (state) => {
            state.holidayListDetails.status = 'idle'
        },
        resetDeleteHolidayDetails: (state) => {
            state.deleteHolidayDetails = {
                status: 'idle',
                data: '',
                error: null
            }
        },
        resetAddHolidayDetails :  (state) => {
            state.addHolidayDetails = {
                status: 'idle',
                data: '',
                error: null
            }
        },
        resetUploadHolidayDetails :  (state) => {
            state.uploadHolidayDetails = {
                status: 'idle',
                data: '',
                error: null
            }
        },
        resetEditHolidayDetails: (state) => {
            state.editHolidayDetails = {
                status: 'idle',
                data: '',
                error: null
            }
        },
        resetBulkDeleteHolidaysDetails: (state) => {
            state.bulkDeleteHolidaysDetails = {
                status: 'idle',
                data: '',
                error: null
            }
        },
    },
    extraReducers: (builder) => builder
        .addCase(getHolidaysList.pending, (state) => {
            state.holidayListDetails.status = 'loading'
        })
        .addCase(getHolidaysList.fulfilled, (state, action: any) => {
            if(action.payload instanceof AxiosError) {
                state.holidayListDetails.status = 'failed'
                state.holidayListDetails.error = (action.payload.response.data.error_messages || []).join(", ")
            } else {
                state.holidayListDetails.status = 'succeeded'
                state.holidayListDetails.data = action.payload.data
            }
        })
        .addCase(getHolidaysList.rejected, (state, action: any) => {
            state.holidayListDetails.status = 'failed'
            state.holidayListDetails.error = action.error.message
        })
        
        .addCase(deleteHoliday.pending, (state) => {
            state.deleteHolidayDetails.status = 'loading'
        })
        .addCase(deleteHoliday.fulfilled, (state, action: any) => {
            if(action.payload instanceof AxiosError) {
                state.deleteHolidayDetails.status = 'failed'
                state.deleteHolidayDetails.error = (action.payload.response.data.error_messages || []).join(", ")
            } else {
                state.deleteHolidayDetails.status = 'succeeded'
                state.deleteHolidayDetails.data = action.payload.data
            }
        })
        .addCase(deleteHoliday.rejected, (state, action: any) => {
            state.deleteHolidayDetails.status = 'failed'
            state.deleteHolidayDetails.error = action.error.message
        })
       
        .addCase(addHoliday.pending, (state) => {
            state.addHolidayDetails.status = 'loading'
        })
        .addCase(addHoliday.fulfilled, (state, action: any) => {
            if(action.payload instanceof AxiosError) {
                state.addHolidayDetails.status = 'failed'
                state.addHolidayDetails.error = (action.payload.response.data.error_messages || []).join(", ")
            } else {
                state.addHolidayDetails.status = 'succeeded'
                state.addHolidayDetails.data = action.payload.data
            }
        })
        .addCase(addHoliday.rejected, (state, action: any) => {
            state.addHolidayDetails.status = 'failed'
            state.addHolidayDetails.error = action.payload || action.error.message
        })

        .addCase(uploadHoliday.pending, (state) => {
            state.uploadHolidayDetails.status = 'loading'
        })
        .addCase(uploadHoliday.fulfilled, (state, action: any) => {
            if(action.payload instanceof AxiosError) {
                state.uploadHolidayDetails.status = 'failed'
                state.uploadHolidayDetails.error = action.payload;
            } else {
                state.uploadHolidayDetails.status = 'succeeded'
                state.uploadHolidayDetails.data = action.payload
            }
        })
        .addCase(uploadHoliday.rejected, (state, action: any) => {
            state.uploadHolidayDetails.status = 'failed'
            state.uploadHolidayDetails.error = action.payload || action.error.message
        })
        
        .addCase(editHoliday.pending, (state) => {
            state.editHolidayDetails.status = 'loading'
        })
        .addCase(editHoliday.fulfilled, (state, action: any) => {
            if(action.payload instanceof AxiosError) {
                state.editHolidayDetails.status = 'failed'
                state.editHolidayDetails.error = (action.payload.response.data.error_messages || []).join(", ")
            } else {
                state.editHolidayDetails.status = 'succeeded'
                state.editHolidayDetails.data = action.payload.data
            }
        })
        .addCase(editHoliday.rejected, (state, action: any) => {
            state.editHolidayDetails.status = 'failed'
            state.editHolidayDetails.error = action.payload || action.error.message
        })
        
        .addCase(bulkDeleteHolidays.pending, (state) => {
            state.bulkDeleteHolidaysDetails.status = 'loading'
        })
        .addCase(bulkDeleteHolidays.fulfilled, (state, action: any) => {
            if(action.payload) {
                state.bulkDeleteHolidaysDetails.status = 'succeeded'
                state.bulkDeleteHolidaysDetails.data = action.payload.data
            } else {
                state.bulkDeleteHolidaysDetails.status = 'failed'
                state.bulkDeleteHolidaysDetails.error = action.payload.message;
            }
        })
        .addCase(bulkDeleteHolidays.rejected, (state, action: any) => {
            state.bulkDeleteHolidaysDetails.status = 'failed'
            state.bulkDeleteHolidaysDetails.error = action.error.message
        })
})
  
export const {resetGetHolidayDetailsStatus, resetDeleteHolidayDetails, resetAddHolidayDetails, resetUploadHolidayDetails, resetEditHolidayDetails, resetBulkDeleteHolidaysDetails } = holidayListSlice.actions

export default holidayListSlice.reducer
